import { useEffect, useRef, useState } from 'react'
import {
  pinMessageListWhileSettling,
  useIsActiveTurn,
  useMessageListScrollActions,
  useMessageListScrollFlags,
} from '@renderer/chat'
import type { ChatViewContext } from './use-chat-view-model'

/** How long the open-scroll keeps re-pinning the bottom while layout settles. */
const INSTANT_SCROLL_PIN_MS = 800

interface ChatScrollTargets {
  showScrollToEnd: boolean
  scrollToEnd: ReturnType<typeof useMessageListScrollActions>['scrollToEnd']
}

/**
 * Where the transcript lands when the view opens, and the jump-to-end/start
 * affordances after that.
 *
 * Every one-shot here waits on `messageListReady`: firing earlier would consume
 * the latch against a list that doesn't exist yet (skeleton still holding the
 * body).
 */
export function useChatScrollTargets(
  context: ChatViewContext | undefined,
  messageListReady: boolean,
  messageCount: number,
): ChatScrollTargets {
  const { showScrollToEnd } = useMessageListScrollFlags()
  const { scrollToEnd, scrollToMessage } = useMessageListScrollActions()
  const isChatLoading = useIsActiveTurn()

  // Hold the button back briefly so it doesn't flash during the settling scroll.
  const [delayedShowScrollToEnd, setDelayedShowScrollToEnd] = useState(false)
  useEffect(() => {
    if (!showScrollToEnd) {
      setDelayedShowScrollToEnd(false)
      return
    }
    const id = setTimeout(() => setDelayedShowScrollToEnd(true), 250)
    return () => clearTimeout(id)
  }, [showScrollToEnd])

  // Instant scroll to bottom on open (e.g. from active chat progress cell or
  // staged-action edit). Fires once when messages first arrive so the
  // messages-query initial fetch lands at the bottom, then stops — allowing
  // the user to scroll freely without being snapped back down. scrollNonce (set
  // by getQuestionChatTarget) re-arms it when the same question reopens into the
  // already-mounted view with instantScrollToBottom unchanged.
  const instantScrollUntilRef = useRef<number | null>(null)
  const hasInitialScrolledRef = useRef(false)
  useEffect(() => {
    if (!context?.instantScrollToBottom) {
      instantScrollUntilRef.current = null
      hasInitialScrolledRef.current = false
      return
    }
    instantScrollUntilRef.current = Date.now() + 1500
    hasInitialScrolledRef.current = false
  }, [context?.instantScrollToBottom, context?.scrollNonce])

  // In refs, not effect-locals: the effect below re-runs on every messageCount
  // change, and letting its cleanup tear the pin down would cut it short
  // mid-settle. Cancelled on unmount, and on re-arm so two pins never overlap.
  const cancelPinRef = useRef<(() => void) | null>(null)
  const armRafRef = useRef(0)
  useEffect(
    () => () => {
      cancelAnimationFrame(armRafRef.current)
      cancelPinRef.current?.()
    },
    [],
  )

  useEffect(() => {
    if (!context?.instantScrollToBottom) return
    if (!messageListReady) return
    if (messageCount === 0) return
    if (hasInitialScrolledRef.current) return
    const until = instantScrollUntilRef.current
    if (until === null || Date.now() > until) return
    hasInitialScrolledRef.current = true
    // One scroll isn't enough: it fires while Virtuoso is still measuring rows
    // and before the view's bottom inset (footer + floating-bar padding) has
    // been committed on the scroller, so a single shot lands short by exactly
    // that padding once scrollHeight catches up. Hand the settle window to the
    // message list's own pin, which follows layout changes until the deadline
    // or the user scrolls up. min = max holds the window open for exactly the
    // deadline — the view keeps growing in bursts, so a quiet-frames release
    // would let go between them.
    armRafRef.current = requestAnimationFrame(() => {
      scrollToEnd({ behavior: 'instant' })
      cancelPinRef.current?.()
      cancelPinRef.current = pinMessageListWhileSettling({
        minMs: INSTANT_SCROLL_PIN_MS,
        maxMs: INSTANT_SCROLL_PIN_MS,
      })
    })
  }, [context?.instantScrollToBottom, context?.scrollNonce, messageListReady, messageCount, scrollToEnd])

  // Surface a specific message on open (e.g. a background AskUserQuestion the
  // user pressed from the actions panel or a toast). Retry per frame until
  // scrollToMessage reports success (the row exists in the DOM), then stop —
  // bounded so it doesn't keep snapping back while the user reads.
  // scrollNonce changes on every open (see getQuestionChatTarget), so reopening
  // the same question re-arms the latch and re-runs the scroll below.
  const hasScrolledToMessageRef = useRef(false)
  useEffect(() => {
    hasScrolledToMessageRef.current = false
  }, [context?.scrollToMessageId, context?.scrollNonce])

  useEffect(() => {
    const targetId = context?.scrollToMessageId
    if (!targetId) return
    if (!messageListReady || messageCount === 0) return
    if (hasScrolledToMessageRef.current) return

    let raf = 0
    const deadline = Date.now() + 3000
    const attempt = () => {
      if (hasScrolledToMessageRef.current) return
      if (scrollToMessage(targetId)) {
        hasScrolledToMessageRef.current = true
        return
      }
      if (Date.now() < deadline) raf = requestAnimationFrame(attempt)
    }
    raf = requestAnimationFrame(attempt)
    return () => cancelAnimationFrame(raf)
  }, [context?.scrollToMessageId, context?.scrollNonce, messageListReady, messageCount, scrollToMessage])

  return {
    showScrollToEnd: delayedShowScrollToEnd && !isChatLoading,
    scrollToEnd,
  }
}
